import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useResponsive } from '@/contexts/ResponsiveContext'
import useAuthStore from '@/stores/authStore'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'

const RegisterPage = () => {
  const navigate = useNavigate()
  const { isMobile } = useResponsive()
  const { register: registerUser, isLoading, error, clearError } = useAuthStore()

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues: {
      username: '',
      password: '',
      confirmPassword: '',
      name: '',
      adminCode: ''
    }
  })

  const password = watch('password')

  useEffect(() => {
    clearError()
  }, [clearError])

  const onSubmit = async (data) => {
    try {
      const { confirmPassword, ...registerData } = data
      await registerUser(registerData)
      toast.success('회원가입 성공!')
      navigate('/dashboard', { replace: true })
    } catch (error) {
      // Error is already handled by the store and displayed via toast
      console.error('Registration error:', error)
    }
  }

  return (
    <div className={isMobile ? 'space-y-4' : 'space-y-6'}>
      <div>
        <h2 className={`text-center font-bold text-gray-900 ${isMobile ? 'text-2xl' : 'text-3xl'}`}>
          관리자 회원가입
        </h2>
        <p className={`mt-2 text-center text-gray-600 ${isMobile ? 'text-sm' : 'text-sm'}`}>
          이미 계정이 있으신가요?{' '}
          <Link
            to="/auth/login"
            className="font-medium text-primary-600 hover:text-primary-500 focus:outline-none focus:underline transition-colors"
          >
            로그인
          </Link>
        </p>
      </div>

      <form className={isMobile ? 'space-y-4' : 'space-y-6'} onSubmit={handleSubmit(onSubmit)}>
        <Input
          label="이름"
          type="text"
          placeholder="이름을 입력하세요"
          autoComplete="name"
          size={isMobile ? 'md' : 'md'}
          error={errors.name?.message}
          {...register('name', {
            required: '이름은 필수입니다',
            minLength: {
              value: 2,
              message: '이름은 최소 2자 이상이어야 합니다'
            }
          })}
        />

        <Input
          label="사용자명"
          type="text"
          placeholder="사용자명을 입력하세요"
          autoComplete="username"
          size={isMobile ? 'md' : 'md'}
          error={errors.username?.message}
          helpText="영문자와 숫자만 사용 가능합니다"
          {...register('username', {
            required: '사용자명은 필수입니다',
            minLength: {
              value: 3,
              message: '사용자명은 최소 3자 이상이어야 합니다'
            },
            pattern: {
              value: /^[a-zA-Z0-9]+$/,
              message: '사용자명은 영문자와 숫자만 사용할 수 있습니다'
            }
          })}
        />

        <Input
          label="관리자 등록 코드"
          type="password"
          placeholder="관리자 등록 코드를 입력하세요"
          size={isMobile ? 'md' : 'md'}
          error={errors.adminCode?.message}
          helpText="관리자로부터 받은 등록 코드를 입력하세요"
          {...register('adminCode', {
            required: '관리자 등록 코드는 필수입니다'
          })}
        />

        <Input
          label="비밀번호"
          type="password"
          placeholder="비밀번호를 입력하세요"
          autoComplete="new-password"
          size={isMobile ? 'md' : 'md'}
          showPasswordToggle={true}
          error={errors.password?.message}
          helpText="최소 8자 이상 입력하세요"
          {...register('password', {
            required: '비밀번호는 필수입니다',
            minLength: {
              value: 8,
              message: '비밀번호는 최소 8자 이상이어야 합니다'
            }
          })}
        />

        <Input
          label="비밀번호 확인"
          type="password"
          placeholder="비밀번호를 다시 입력하세요"
          autoComplete="new-password"
          size={isMobile ? 'md' : 'md'}
          showPasswordToggle={true}
          error={errors.confirmPassword?.message}
          {...register('confirmPassword', {
            required: '비밀번호 확인은 필수입니다',
            validate: value => value === password || '비밀번호가 일치하지 않습니다'
          })}
        />

        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className={`text-red-700 ${isMobile ? 'text-sm' : 'text-sm'}`}>
              {error}
            </div>
          </div>
        )}

        <Button
          type="submit"
          variant="primary"
          size={isMobile ? 'lg' : 'md'}
          fullWidth={true}
          loading={isSubmitting || isLoading}
          disabled={isSubmitting || isLoading}
        >
          {isSubmitting || isLoading ? '계정 생성 중...' : '계정 생성'}
        </Button>
      </form>

      {/* Demo admin code for development */}
      {process.env.NODE_ENV === 'development' && (
        <div className={`p-4 bg-yellow-50 border border-yellow-200 rounded-md ${isMobile ? 'mt-4' : 'mt-6'}`}>
          <h3 className={`font-medium text-yellow-800 mb-2 ${isMobile ? 'text-sm' : 'text-sm'}`}>
            데모 관리자 코드
          </h3>
          <div className={`text-yellow-700 ${isMobile ? 'text-xs' : 'text-xs'}`}>
            <p><strong>관리자 코드:</strong> ADMIN_SECRET_2024</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default RegisterPage